/** English translations for game content, keyed by ID (see src/i18n/index.ts). */

export interface LocalizedText { name: string; desc: string }
export interface LocalizedClass { name: string; title: string; role: string; blurb: string }

/** Class names shown in English. */
export const CLASS_EN: Record<string, LocalizedClass> = {
  strength: {
    name: 'Gym Rat',
    title: 'Muscle',
    role: 'Tank / Heavy Melee',
    blurb: 'Built like a dumbbell. Holds the gate and shrugs off the whole first wave without losing HP.',
  },
  agility: {
    /*
     * ⚠️⚠️ TÊN EN CỐ Ý KHÁC TÊN VI — ĐỪNG "sửa cho khớp".
     *
     * Người dùng chốt 2 lần ở 2 thời điểm khác nhau:
     *   · *"Gái ngành Tiếng Anh để là `Baby three` đi"* → bản EN giữ `Baby three`
     *   · *"Tiếng Việt đổi Baby three thành Gái ngành"* → bản VI đổi thành `Gái ngành`
     *     (xem `data/classes.ts`, class `agility`)
     * ⇒ KHÁC NHAU LÀ CHỦ Ý. Sửa một bên theo bên kia là làm SAI yêu cầu người dùng.
     *
     * Viết hoa chữ `t` thường (không phải "Baby Three") — đúng yêu cầu, dù các class khác viết hoa
     * kiểu Title Case. Test `i18n.test.ts` chỉ kiểm tra "không có dấu tiếng Việt" và độ dài ≤26 ký
     * tự, nên "Baby three" (10 ký tự) hợp lệ.
     */
    name: 'Baby three',
    title: 'Femme Fatale',
    role: 'Charming Assassin / High Dodge',
    blurb: 'Her smile is so lovely that nobody remembers to run. Paper-thin HP, yet no one has ever touched her.',
  },
  street: {
    name: 'Market Loudmouth',
    title: 'Gossipmonger',
    role: 'Crowd Control / AoE Thrower',
    blurb: 'A market tongue that humiliates monsters, plus a sandal that hits the whole crowd.',
  },
  army: {
    name: 'Hired Gun',
    title: 'Mercenary',
    role: 'Long-Range Marksman',
    blurb: 'Pay up and he shoots, run out of coin and he naps. Huge damage, but bullets cost gold.',
  },
  mother: {
    name: 'Rent-a-Womb',
    title: 'Summoner',
    role: 'Summoner / Long-Range Egg Thrower',
    blurb: 'Takes orders, delivers births, throws hen eggs in her spare time. Low stats, but one kid every 10s - numbers win.',
  },
  /*
   * PLACEHOLDER — 2 class "chưa mở khoá" (Thợ mộc/ Thợ lặn). Người dùng chốt *"design, skill,
   * mọi thứ mình sẽ làm sau"* ⇒ tên + mô tả ở đây là TẠM, dịch thẳng từ bản Việt trong
   * `src/data/classes.ts`. Test i18n bắt buộc: đủ 4 trường, KHÔNG dấu tiếng Việt, số liệu khớp.
   */
  carpenter: {
    name: 'Carpenter',
    title: 'Wood Hammer',
    role: 'Melee / Barricade Builder',
    blurb: 'Drags the whole workshop to the fight. His hammer pounds like a pile driver - placeholder stats.',
  },
  diver: {
    name: 'Diver',
    title: 'Air Tank',
    role: 'Ranged / Deep Flanker',
    blurb: 'Dives under, then surfaces behind the monsters. Placeholder stats, built like a high-dodge assassin.',
  },
};

/** 7 base skills per class, merged into ONE table keyed by skill id. */
export const SKILL_EN: Record<string, LocalizedText> = {
  // ---------------------------------------------------------------- strength
  ironSkin: { name: 'Iron Flex', desc: 'Flex hard: -40% damage taken for 6s' },
  powerFist: { name: '100kg Dumbbell Punch', desc: 'Slam a dumbbell through the floor: 260% damage around you' },
  taunt: { name: 'Gun Show Taunt', desc: 'Flex so hard every enemy within 260px charges you for 5s' },
  quake: { name: 'G+ Floor Stomp', desc: 'Stomp that shakes the whole gym: 180% damage + slow around you' },
  bulwark: { name: 'Whey Shield', desc: 'Chug whey: shield for 45% max HP for 8s' },
  unstoppable: { name: 'Muscle Overload', desc: 'Final set: invulnerable for 4s' },
  dragonStrike: { name: 'Ultimate: Unmatched Force', desc: 'Ultimate: 700% area damage' },

  // ---------------------------------------------------------------- agility
  flurry: { name: 'Four-Beat Dance', desc: 'A four-beat dance - each beat is a kick: 240% damage total' },
  shadowStep: { name: 'Whisper Step', desc: 'Lean in, whisper, then glide: dash 220px, 200% damage' },
  smoke: { name: 'Hair Veil', desc: 'Sweep hair over the face: +45% dodge for 6s' },
  shuriken: { name: 'Five Flowers Gift', desc: 'Gift 5 flowers - 90% damage each' },
  afterimage: { name: 'Blown Kiss', desc: 'Blow a kiss for 2.5s: untouchable, anyone who touches you takes 150% damage back' },
  haste: { name: 'Catwalk Stride', desc: 'Strut across the floor: +70% attack speed and move speed for 7s' },
  thousandHands: { name: 'Ultimate: Belle Dance', desc: 'Ultimate: a 12-beat dance - 130% damage per beat' },

  // ---------------------------------------------------------------- street
  trashTalk: { name: 'Spirit Crush', desc: 'Cut enemy damage by 35% within 240px for 6s' },
  sandalThrow: { name: 'Sandal Throw', desc: 'Throw a sandal at 4 enemies, 120% each' },
  marketRumor: { name: 'Market Rumor', desc: 'Slow enemy move speed by 45% for 7s' },
  veggieBarrage: { name: 'Veggie Rain', desc: 'Hurl 8 items, 85% damage each' },
  fishSauce: { name: 'Toxic Fish Sauce', desc: 'AoE 200% + -50% enemy armor for 8s' },
  gossipShield: { name: 'Gossip Shield', desc: 'Shield for 35% HP for 8s' },
  marketChaos: { name: 'Market Brawl', desc: 'Ultimate: 550% area damage + stun for 4s' },

  // ---------------------------------------------------------------- army
  suppressive: { name: 'Suppressive Fire', desc: 'Full-auto 6 rounds, 95% each' },
  grenade: { name: 'Black-Market Grenade', desc: 'AoE 260% within 130px' },
  armorPiercing: { name: 'Armor-Piercing', desc: 'Ignore 100% enemy armor on the next hit' },
  artillery: { name: 'Hired Artillery', desc: '3 shells, 170% each' },
  entrench: { name: 'Dig In', desc: '+60% armor, +25% attack speed for 8s' },
  ammoDrop: { name: 'Ammo Airdrop', desc: 'Instantly receive 40 rounds' },
  finalSalvo: { name: 'Ultimate: Full Mag', desc: 'Ultimate: 16 piercing rounds, 120% each' },

  // ---------------------------------------------------------------- mother
  giveBirth: { name: 'Rush Order Birth', desc: 'Deliver 1 kid right now' },
  scold: { name: 'Neighborhood Scold', desc: 'AoE 150% + -30% enemy attack speed for 5s' },
  favoritism: { name: 'Home Care Boost', desc: '+50% HP and armor for the whole squad for 8s' },
  hordeCall: { name: 'Bulk Birth', desc: 'Deliver 6 kids' },
  motherWrath: { name: 'Busted Order', desc: 'AoE 320% around you' },
  endlessLine: { name: 'Long-Term Contract', desc: 'For 10s, deliver 1 kid every 2s' },
  grandMother: { name: 'Ultimate: Birth Factory', desc: 'Ultimate: deliver 12 elite kids' },

  // ------------------------------------------- carpenter (PLACEHOLDER, xem chú thích CLASS_EN)
  hammerSmash: { name: 'Wood Hammer', desc: 'Slam the hammer down: 240% damage around you' },
  nailDrive: { name: 'Nail Drive', desc: 'Drive nails through them: 130% damage' },
  sawSplit: { name: 'Split Saw', desc: 'Saw them in half: 200% damage within 130px' },
  woodWard: { name: 'Plank Ward', desc: 'Raise a plank wall: +55% armor for 8s' },
  sawdust: { name: 'Sawdust Cloud', desc: 'Blind them with sawdust: -45% move speed for 6s' },
  woodenShield: { name: 'Ironwood Shield', desc: 'Ironwood shield: block 40% max HP for 8s' },
  ultimateSaw: { name: 'Ultimate: Workshop Blast', desc: 'Ultimate: 580% area damage' },

  // ------------------------------------------------ diver (PLACEHOLDER, xem chú thích CLASS_EN)
  airTank: { name: 'Air Tank', desc: 'Fill the tank: +55% attack speed for 7s' },
  spearGun: { name: 'Spear Gun', desc: 'Fire the spear gun: 140% damage' },
  deepDive: { name: 'Deep Dive', desc: 'Dive under then surface: dash 230px, 210% damage' },
  harpoonNet: { name: 'Harpoon Net', desc: 'Cast a harpoon net: -50% move speed for 6s' },
  breathHold: { name: 'Breath Hold', desc: 'Hold your breath 3s: untouchable, touch you for 160% back' },
  flipperKick: { name: 'Flipper Kick', desc: 'Flipper kick: 190% damage around you' },
  ultimateDepth: { name: 'Ultimate: Deep Seabed', desc: 'Ultimate: 520% area damage' },
};

/**
 * ULTIMATE — skill thứ 8, người chơi BẤM TAY, hồi 3 phút (xem `src/data/ultimates.ts`).
 *
 * ⚠️ Bản Việt của bảng này CHÍNH LÀ `ULTIMATES[classId].desc` (dữ liệu trong `data/`), không phải
 * một chuỗi chép tay ở chỗ khác ⇒ tra theo **`id`** giống mọi bảng nội dung khác.
 * Ràng buộc y như chiêu thường: GIỮ NGUYÊN mọi con số của bản Việt, tên ≤ 26 ký tự, không dấu.
 */
export const ULT_EN: Record<string, LocalizedText> = {
  ultGiantBoar: {
    name: 'Giant Boar',
    desc: 'Turn into a giant boar for 15s: huge ATK / HP / armor but far slower attacks. Basic attacks become an AoE roar that stuns (never stuns bosses); every 2s you swallow 1 focused enemy (never bosses).',
  },
  ultClone: {
    name: 'Clone Squad',
    desc: 'Create 3 identical clones spread across the map for 20s, with much faster attacks. Clones cast skills on their own - you only control the original.',
  },
  ultFirebomb: {
    name: 'Firebomb Run',
    desc: 'Sprint around throwing firebombs for 20s: 1 bomb per zone, then move to another zone, always picking the zone with the most enemies. Bombs burn and shred armor.',
  },
  ultAirstrike: {
    name: 'Call Plane',
    desc: 'Call a plane that flies back and forth dropping bombs exactly where it passes, for 20s.',
  },
  ultSubjugate: {
    name: 'Subjugate',
    desc: 'Convert 20% of the enemies currently on screen into your troops (random pick, bosses excluded). No countdown - it only ends when a converted unit dies; when it expires or the stage ends they turn back into enemies. Converted troops never count toward the 15-kid cap.',
  },

  // ------------------------------ carpenter + diver (PLACEHOLDER, xem ULTIMATES ở data/ultimates.ts)
  ultWoodTitan: {
    name: 'Wood Titan',
    desc: 'Turn into a giant wood titan for 15s: huge armor and HP but far slower attacks. Basic attacks become an AoE pound that stuns (never stuns bosses); every 2s you swallow 1 focused enemy (never bosses).',
  },
  ultDiveClones: {
    name: 'Water Clones',
    desc: 'Create 3 water clones spread across the map for 20s, with much faster attacks. Clones cast skills on their own - you only control the original.',
  },
};

/** 15 craftable extra skills. */
export const EXTRA_SKILL_EN: Record<string, LocalizedText> = {
  // ---------------------------------------------------------------- strength
  kimCangChuong: { name: 'Flex Palm', desc: 'Flex palm: 300% damage within 140px around you' },
  hoTheLaHan: { name: 'Lifting Belt', desc: 'Shield for 35% max HP for 7s (cheap, fast cooldown)' },
  suTuHong: { name: 'Gym Roar', desc: 'Roar across the gym: -25% enemy damage within 220px for 6s' },

  // ---------------------------------------------------------------- agility
  loiDinhCuoc: { name: 'Lotus Heel Spin', desc: 'Spin once on a lotus heel: 220% damage within 100px' },
  phanThanCham: { name: 'Glide To You', desc: 'Glide right up to them: 240% damage, 260px range' },
  vanDaiBo: { name: 'Silk Trail', desc: 'Silk flows with every step: +35% move speed for 8s' },

  // ---------------------------------------------------------------- street
  tungHoaDiem: { name: 'Flinging Flames', desc: 'Fling 5 fistfuls of fire, 140% each, 340px range' },
  khauChienQuanHung: { name: 'Crowd Curse-Out', desc: 'A long rant: -40% enemy attack speed within 260px for 8s' },
  luoiCa: { name: 'Fishing Net Trap', desc: 'Cast a fish net: -70% move speed within 220px for 4s' },

  // ---------------------------------------------------------------- army
  khongKich: { name: 'Call Airstrike', desc: 'Call an airstrike: 340% damage within 180px' },
  haoChienDau: { name: 'Battle Trench', desc: 'Dig a trench: shield for 40% max HP for 9s' },
  danKhoi: { name: 'Smoke Round', desc: 'Throw a smoke round: -40% enemy damage within 230px for 6s' },

  // ---------------------------------------------------------------- mother
  goiHangXom: { name: 'Call Neighbors', desc: 'Call 4 neighbors to fight for you (40s)' },
  choiTreTroiGiang: { name: 'Bamboo Broom Fall', desc: 'A bamboo broom crashes down: 290% damage within 200px' },
  nuocMatCaSau: { name: 'Crocodile Tears', desc: 'Cry and whine, instantly heal 420 HP' },
};

/** 15 stages, keyed by stage id ('1.1' ... '3.5'). */
export const MAP_EN: Record<string, { name: string; blurb: string }> = {
  '1.1': { name: 'Martial Market Alley', blurb: 'Three thugs block the alley demanding a toll. Please leave your sandals.' },
  '1.2': { name: 'Village Courtyard', blurb: 'The whole village comes out to watch, nobody leaves, and nobody helps either.' },
  '1.3': { name: 'Backyard Veggie Patch', blurb: 'The lady selling "greens" - turns out it is all clubs and sandals.' },
  '1.4': { name: 'Stone Bridge Gate', blurb: 'Armed guards on duty. No papers, no crossing.' },
  '1.5': { name: 'Martial Village Gate', blurb: 'The neighborhood boss appears, holding... a legendary sandal.' },
  '2.1': { name: 'Riot at the Market', blurb: 'Two gangs fight over stalls. You stand in the middle - great spot you picked.' },
  '2.2': { name: 'Roadside Tea Stall', blurb: 'Even sitting down for iced tea gets you a visit from monsters.' },
  '2.3': { name: 'Weapons Foundry', blurb: 'The monsters here wear real armor; every hit takes forever to land.' },
  '2.4': { name: 'Windy Pass Road', blurb: 'Wind blows even the fire pans away - heavy ranged damage everywhere.' },
  '2.5': { name: 'County Magistrate Office', blurb: 'The magistrate sold the gate to the demon cult. Smash it too.' },
  '3.1': { name: 'Cult Headquarters Gate', blurb: 'A bright red gate, with... sandals of the defeated hanging on both sides.' },
  '3.2': { name: 'Night Fight Ring', blurb: 'Lanterns, war drums, and a very rude crowd of gamblers.' },
  '3.3': { name: 'Cult Wine Cellar', blurb: 'The monsters are drunk, so they stagger a lot but hit very hard.' },
  '3.4': { name: 'Snow Peak Summit', blurb: 'So cold that every skill runs half a beat slower.' },
  '3.5': { name: 'Demon Cult HQ', blurb: 'Final boss: he calls himself the "righteous path". Whatever.' },
};

/** 3 difficulties. */
export const DIFF_EN: Record<string, { name: string; blurb: string }> = {
  easy: { name: 'Easy', blurb: 'Light monsters, low rewards - to learn the ropes and unlock more.' },
  normal: { name: 'Normal', blurb: 'The intended baseline balance of the game.' },
  elite: { name: 'Elite', blurb: 'Monsters are far tankier and hit much harder - double rare drop rate in return.' },
};

/** Enemy names keyed by key in `ENEMIES`. */
export const ENEMY_EN: Record<string, string> = {
  thug: 'Neighborhood Thug',
  runner: 'Footwork Fiend',
  brawler: 'Market Bruiser',
  gunner: 'Rifleman',
  spawner: 'Brood Merchant',
  brat: 'Offspring',
  eliteBrat: 'Golden Child',
};

/** Boss affix names keyed by id in `BOSS_AFFIXES`. */
export const BOSS_AFFIX_EN: Record<string, string> = {
  armored: 'Ironclad',
  frenzy: 'Frenzy',
  summoner: 'Summoner',
  reflect: 'Reflect',
  regen: 'Regeneration',
};

/** Stage boss name (`STAGE_BOSS`). */
export const STAGE_BOSS_EN = 'Locomotive King';

/** 3 potions. */
export const POTION_EN: Record<string, LocalizedText> = {
  hp: { name: 'Health Potion', desc: 'Instantly restore 45% max HP' },
  mana: { name: 'Mana Potion', desc: 'Instantly restore 60% max mana' },
  stamina: { name: 'Stamina Potion', desc: 'INVULNERABLE for 5s + 60% power boost. Max 2 potions per stage.' },
};

/**
 * GOI Y TUNG WAVE (10 wave) — hien o dong `#tip` khi dang danh.
 * Danh so dong de tra theo chi so wave (1..10); thieu thi roi ve chu tieng Viet.
 */
export const WAVE_TIP_EN: string[] = [
  'Warm-up: one fighter clears the whole neighbourhood without losing a drop of blood.',
  'Fast runners show up now.',
  'Footwork fiends dodge a lot — you need area damage.',
  'Market bruisers throw things from range.',
  'Riflemen hurt — do not stand outside their reach.',
  'Brood merchants spawn monsters — kill them first.',
  'Three factions mixed together.',
  'Pressure from both speed and range.',
  'Elite wave — hold the House at all costs.',
  'ALL-OUT: the stage boss arrives.',
];
